import { CONFLICT } from 'http-status-codes';
import { get, head, pickBy, find, isEmpty } from 'lodash';
import moment from 'moment';
import { getFromAccountById, getToAccountById, getAccountCurrency } from '../accounts/selectors';
import { dateFormat, calendarTypes, TRANSACTION_LIMIT_ERROR_CODE, currenciesForCalender } from '../../config';
import currencyMapping from '../../config/currencyMapping';
import chinaReasonCode from '../../config/chinaReasonCode';
import UAEReasonCode from '../../config/UAEReasonCode';
import { isEEACountry, getCurrencyDecimalPlaces } from '../../utils/countryUtils';
import { formatAmount, getAmountWithCurrency, getCurrencySymbol, getWhoPaysLabel } from '../../helpers/fieldHelpers';
import { formatAccountDetailsLine } from '../../utils/accountUtils';
import content from '../../ift/components/IFTPayments/iftPayments.content';
import { CONST_AMOUNT_UPPERCASE, CONST_REMITTANCE, CONST_INSTRUCTED, CONST_EQUIVALENT } from '../../config/constants';
import {
	getPayeeById,
	unauthorisedDailyLimitFilter,
	unauthorisedTransactionLimitFilter,
	toFormattedDate,
	toDistinctDates,
	fromPaymentToDateListItem,
} from '../commonSelectors/selectors';

const getPopularCurrency = (currency) => {
	const isPopularCurrency = get(currency, 'to_currency');
	if (isPopularCurrency.match(/^(EUR|USD)$/)) {
		return 'popular';
	}
	return '';
};
const currenciesForReasonCode = ['AED', 'CNY'];
export const getIFTPayments = (state) => get(state, 'ift.payments', []);
export const getIFTSelectedPayment = (state) => get(state, 'ift.selectedPayment', {});

export const getIFTPayment = (state, paymentId) => getIFTPayments(state).find((payment) => payment.id === paymentId);
export const getIFTPaymentIds = (state) => getIFTPayments(state).map((payment) => payment.id);
export const getShowAddPaymentButton = () => true;
export const getFirstPayment = (state) => head(getIFTPayments(state));

export const getIFTCalendarType = (state) => {
	const { sendCurrency } = getIFTSelectedPayment(state);
	if (currenciesForCalender.includes(sendCurrency)) {
		return sendCurrency.toLowerCase();
	}
	return calendarTypes.CALENDAR_TYPE_EXOTIC;
};

export const getCurrencyList = (state) => {
	const fromAccountId = get(state, 'ift.selectedPayment.fromAccount', '');
	const fromCurrency = getAccountCurrency(state, fromAccountId);
	return currencyMapping
		.filter((currency) => currency.from_currency === fromCurrency)
		.map((currency) =>
			pickBy({
				id: currency.to_currency,
				label: currency.to_currency_name,
				shortCode: currency.to_currency,
				group: getPopularCurrency(currency),
				mandatoryPrebookedRate: currency.pre_booked_rate_mandatory,
			})
		);
};

export const isBeneficiaryFromEEACountries = (state) => {
	const toAccountId = get(state, 'ift.selectedPayment.toAccount', '');
	const payee = getPayeeById(state, toAccountId);
	return isEEACountry(get(payee, 'bank.address.country'));
};

const isPaymentInValid = (payment) => {
	if (!payment.sendAmount) return true;
	return false;
};

export const getInvalidPaymentIds = (state) =>
	getIFTPayments(state)
		.filter(isPaymentInValid)
		.map((payment) => payment.id);

const isPaymentRateIndicative = (payment) => !payment.isPreBookedRate;

export const getSomePaymentRatesAreIndicative = (state) => getIFTPayments(state).some(isPaymentRateIndicative);

const isTransactionLimitError = (authorisation) => get(authorisation, 'error.code', 0) === TRANSACTION_LIMIT_ERROR_CODE;
const isConflict = (authorisation) => get(authorisation, 'responseCode', 0) === CONFLICT;
export const isAuthMaxTransactionLimitError = (authorisation) =>
	isConflict(authorisation) && isTransactionLimitError(authorisation);

export const isResponseMaxTransactionLimitError = (apiResponse) =>
	isTransactionLimitError(apiResponse.body) && get(apiResponse, 'code', 0) === CONFLICT;

export const getFromCurrency = (state) => {
	const fromAccountId = get(state, 'ift.selectedPayment.fromAccount', '');
	return getAccountCurrency(state, fromAccountId);
};
export const getDebitOrCreditAmount = (paymentAmount = [], currency) =>
	paymentAmount.find(
		(amount = {}) => amount.currency === currency && [CONST_INSTRUCTED, CONST_EQUIVALENT].includes(amount.type)
	);

export const getPaymentAmount = (rateDetails, fromCurrency, sendCurrency) => {
	const credit = getDebitOrCreditAmount(rateDetails.payment_amount, sendCurrency);
	const debit = getDebitOrCreditAmount(rateDetails.payment_amount, fromCurrency);
	const remittanceAmount = find(rateDetails.payment_amount, { type: CONST_REMITTANCE });
	const creditCurrency = get(credit, 'currency', '');
	const debitCurrency = get(debit, 'currency', '');
	return {
		creditAmountWithCurrency: getAmountWithCurrency(creditCurrency, get(credit, 'value', '')),
		debitAmountWithCurrency: getAmountWithCurrency(debitCurrency, get(debit, 'value', '')),
		creditAmount: formatAmount(get(credit, 'value', ''), getCurrencyDecimalPlaces(creditCurrency)),
		debitAmount: formatAmount(get(debit, 'value', ''), getCurrencyDecimalPlaces(debitCurrency)),
		remittanceAmount: getAmountWithCurrency(get(remittanceAmount, 'currency', ''), get(remittanceAmount, 'value', '')),
	};
};
const paymentFees = (rateDetails) => {
	const chargeAmount = find(rateDetails.charge_amount, { type: CONST_AMOUNT_UPPERCASE });
	const creditCurrency = get(chargeAmount, 'currency', '');
	return `${getCurrencySymbol(creditCurrency)}${formatAmount(get(chargeAmount, 'value', ''))} ${creditCurrency}`;
};

export const getRateInformation = (state) => {
	const { rates: ratesInformation } = getIFTSelectedPayment(state);
	if (!isEmpty(ratesInformation)) {
		const rateDetails = head(ratesInformation.details);
		const fromCurrency = getFromCurrency(state);
		const sendCurrency = get(state, 'ift.selectedPayment.sendCurrency', '');
		return {
			...getPaymentAmount(rateDetails, fromCurrency, sendCurrency),
			exchangeRate: get(ratesInformation, 'fx.rate', ''),
			paymentFees: paymentFees(rateDetails),
			settlementDate: moment(get(rateDetails, 'value_date', '')).format(dateFormat),
			submissionDate: moment(get(rateDetails, 'submission_date', '')).format(dateFormat),
			isSettlementDateUpdated: get(rateDetails, 'value_date_updated', ''),
			isMissedCurrency: get(rateDetails, 'advise_for_prebook', ''),
		};
	}
	return {};
};
export const getPaymentListForTable = (state) => {
	const payments = getIFTPayments(state);
	const hasDelete = payments.length > 1;

	return payments.reduce((accumulator, payment) => {
		const fromAccount = getFromAccountById(state, payment.fromAccount);
		const toAccount = getToAccountById(state, payment.toAccount);
		const { sendCurrency, rates } = payment;
		const fromCurrency = getAccountCurrency(state, payment.fromAccount);
		const { creditAmountWithCurrency } = getPaymentAmount(get(rates, 'details[0]', []), fromCurrency, sendCurrency);
		const error = isAuthMaxTransactionLimitError(payment.authorisation);

		const newRow = [
			{
				rowId: payment.id,
				label: fromAccount.displayName,
				accountHolder: fromAccount.metadata.account_name,
				subLabel: formatAccountDetailsLine(fromAccount.sortCode, fromAccount.accountNumber),
			},
			{
				rowId: payment.id,
				label: toAccount.displayName,
				subLabel: toAccount.accountNumber ? `${toAccount.accountNumber}` : toAccount.iban,
			},
			{
				rowId: payment.id,
				label: creditAmountWithCurrency,
				isHighlighted: !payment.isPreBookedRate,
				error: error ? content.maxLimitExceededMessage : undefined,
			},
			{
				rowId: payment.id,
				label: payment.reason,
			},
			{
				rowId: payment.id,
				label: payment.date ? moment(payment.date).format(dateFormat) : '',
			},
			{
				rowId: payment.id,
				hasDelete,
				hasEdit: true,
			},
		];
		return [...accumulator, ...newRow];
	}, []);
};
export const isFromSameCurrency = (state) => {
	const fromCurrency = getFromCurrency(state);
	const toCurrency = get(state, 'ift.selectedPayment.sendCurrency', '');
	return toCurrency === fromCurrency;
};

const getDisplayFrom = (state, payment) => {
	const fromAccount = getFromAccountById(state, payment.fromAccount);
	return {
		type: 'from',
		header: fromAccount.displayName,
		body: formatAccountDetailsLine(fromAccount.sortCode, fromAccount.accountNumber),
		footer: '',
		accountHolder: fromAccount.metadata.account_name,
	};
};

const getDisplayTo = (state, payment) => {
	const payee = getPayeeById(state, payment.toAccount);
	return {
		type: 'to',
		header: payee.display_name,
		body: get(payee, 'to_account.iban') || get(payee, 'to_account.account_number', ''),
		footer: get(payee, 'address.country', ''),
	};
};

const getDisplayAmountSent = (payment, fromCurrency, rates) => {
	const { remittanceAmount } = getPaymentAmount(rates, fromCurrency, payment.sendCurrency);
	if (!remittanceAmount) return { type: 'amountSent', body: '', isHighlighted: false };
	return { type: 'amountSent', body: remittanceAmount, isHighlighted: isPaymentRateIndicative(payment) };
};

const getDisplayDebitAmount = (payment, fromCurrency, rates) => {
	const { debitAmountWithCurrency } = getPaymentAmount(rates, fromCurrency, payment.sendCurrency);
	if (!debitAmountWithCurrency) return { type: 'debitAmount', body: '', isHighlighted: false };
	return { type: 'debitAmount', body: debitAmountWithCurrency, isHighlighted: isPaymentRateIndicative(payment) };
};

const getDisplayExchangeRate = (payment) => {
	const body = get(payment, 'rates.fx.rate', '');
	return {
		type: 'exchangeRate',
		body,
		isHighlighted: isPaymentRateIndicative(payment),
	};
};

const getDisplayFees = (payment, rates) => {
	const chargingModel = get(payment, 'chargingModal', '');
	const whoPayslabel = getWhoPaysLabel(chargingModel);

	const body = `${paymentFees(rates)} (${whoPayslabel})`;

	return {
		type: 'fees',
		body,
	};
};

const formatDateForReview = (date) => {
	if (!date) return '';
	return moment(date).format(dateFormat);
};

const formatPaymentForDisplayTable = (state, payment) => {
	const fromCurrency = getAccountCurrency(state, payment.fromAccount);
	const rates = get(payment, 'rates.details[0]');
	return [
		getDisplayFrom(state, payment),
		getDisplayTo(state, payment),
		getDisplayAmountSent(payment, fromCurrency, rates),
		getDisplayDebitAmount(payment, fromCurrency, rates),
		getDisplayExchangeRate(payment),
		getDisplayFees(payment, rates),
		{
			type: 'subDate',
			body: formatDateForReview(get(payment, 'rates.details[0].submission_date', '')),
		},
		{
			type: 'settlementDate',
			body: formatDateForReview(get(payment, 'rates.details[0].value_date', '')),
		},
		{
			type: 'reason',
			body: payment.reason,
		},
	];
};

const getReviewAuthInfoForDisplayTable = (payment) => {
	const statusTitle = 'Authorisation required.';
	const authorisers = get(payment, 'authorisers', []);
	const authorisersGroup = get(payment, 'authorisersGroup', []);

	return { statusTitle, authorisers, authorisersGroup };
};

const getReviewPaymentsForDisplayTable = (state) => (payment) => {
	const tableData = formatPaymentForDisplayTable(state, payment);
	const authorisation = getReviewAuthInfoForDisplayTable(payment);
	const isDefaultAuthorisers = get(payment, 'isDefaultAuthorisers', false);
	return { tableData, ...authorisation, isDefaultAuthorisers, id: payment.id };
};

export const getIFTPaymentsForReview = (state) => getIFTPayments(state).map(getReviewPaymentsForDisplayTable(state));

// const awaitingAuthorisationFilter = ({ confirmation: { responseCode = '' } = {} }) => responseCode === 401;
const successOrAuthFilter = ({ confirmation: { responseCode = '' } = {} }) =>
	responseCode === 401 || responseCode === 202;

const getConfirmedAuthInfoForDisplayTable = (payment) => {
	const statusContent = '';
	const statusTitle = 'Authorisation required.';
	const authorisers = get(payment, 'authorisers', []);
	const authorisersGroup = get(payment, 'authorisersGroup', []);
	const authType = get(payment, 'confirmation.authType');
	const paymentStatus = get(payment, 'authorisation.status');

	return { statusContent, statusTitle, authorisers, authorisersGroup, authType, paymentStatus };
};

const getConfirmedPaymentsForDisplayTable = (state) => (payment) => {
	const tableData = formatPaymentForDisplayTable(state, payment);
	const authorisation = getConfirmedAuthInfoForDisplayTable(payment);
	return { tableData, ...authorisation };
};

export const getIFTConfirmedAwaitingAuthPayments = (state) =>
	getIFTPayments(state).filter(successOrAuthFilter).map(getConfirmedPaymentsForDisplayTable(state));

const failureCodes = [500, 409, 400, 404];
const failedFilter = ({ confirmation: { responseCode = '' } = {} }) => failureCodes.includes(responseCode);

const getFailedInfoForDisplayTable = (payment) => {
	const statusContent = get(payment, 'confirmation.error.message');
	const statusTitle = 'Payment failed.';

	return { statusContent, statusTitle };
};

const getFailedPaymentsForDisplayTable = (state) => (payment) => {
	const tableData = formatPaymentForDisplayTable(state, payment);
	const failed = getFailedInfoForDisplayTable(payment);
	return { tableData, ...failed };
};

export const getIsEditing = (state) => get(state, 'ift.editing', false);

export const getIFTFailedPayments = (state) =>
	getIFTPayments(state).filter(failedFilter).map(getFailedPaymentsForDisplayTable(state));

export const showDefaultCurrency = (state) => {
	const { toAccount, sendCurrency } = getIFTSelectedPayment(state);
	const { default_currency } = getPayeeById(state, toAccount);
	return sendCurrency !== default_currency;
};
export const isExoticCurrency = (state) => {
	const { sendCurrency } = getIFTSelectedPayment(state);
	return !currenciesForCalender.includes(sendCurrency);
};
export const isPreBookedRateMandatory = (state) => {
	const { sendCurrency } = getIFTSelectedPayment(state);
	if (isExoticCurrency(state)) {
		return getCurrencyList(state).some(
			(currency) => currency.id === sendCurrency && currency.mandatoryPrebookedRate === 'Y'
		);
	}
	return false;
};

export const getIFTPaymentsRequireAuthorisers = (state) => {
	const payments = getIFTPayments(state);
	return payments.some((payment) => get(payment, 'authorisers', []).length === 0);
};

export const getIFTPaymentsRequireAuthorisersGroup = (state) => {
	const payments = getIFTPayments(state);
	return payments.some((payment) => get(payment, 'authorisersGroup', []).length === 0);
};

export const getIFTSingleAuthorisersList = (state, paymentId) =>
	get(
		getIFTPayments(state).find((payment) => payment.id === paymentId),
		'singleAuthoriserList',
		[]
	);

export const getIFTMultiAuthorisersList = (state, paymentId) =>
	get(
		getIFTPayments(state).find((payment) => payment.id === paymentId),
		'multiAuthoriserList',
		[]
	);

const reasonCodeRows = (reasonCode, index) => ({
	id: index,
	label: reasonCode.reason_code,
	value: reasonCode.description,
});
const reasonMapping = { CNY: chinaReasonCode, AED: UAEReasonCode };
export const getReasonCodeList = (state) => {
	const { sendCurrency } = getIFTSelectedPayment(state);
	return get(reasonMapping, sendCurrency, []).map((reasonCode, index) => reasonCodeRows(reasonCode, index));
};
export const checkCountryCodeForExoticCurrency = (state) => {
	const { sendCurrency } = getIFTSelectedPayment(state);
	return currenciesForReasonCode.includes(sendCurrency);
};

export const getPaymentCount = (state) => getIFTPayments(state).length;
export const getIsPaymentListEdit = (state) => get(state, 'ift.isPaymentListEdit', false);

// eslint-disable-next-line
export const isFutureDatedPayment = (selectedSubmissionDate, calendarSubmissionDate) =>
	moment.isMoment(selectedSubmissionDate)
		? selectedSubmissionDate.isAfter(calendarSubmissionDate.startOf('day'))
		: false;

export const getIFTDailyLimitModalDates = (state) =>
	getIFTPayments(state)
		.filter(unauthorisedDailyLimitFilter)
		.map(toFormattedDate)
		.reduce(toDistinctDates, [])
		.map(fromPaymentToDateListItem);

export const getIFTPaymentsWithDailyLimitError = (state) => getIFTPayments(state).some(unauthorisedDailyLimitFilter);

export const getShowIFTDailyLimitModal = (state) => {
	if (getIsEditing(state)) {
		return (
			getIFTPayments(state).some(unauthorisedDailyLimitFilter) ||
			getIFTPayments(state).some(unauthorisedTransactionLimitFilter)
		);
	}
	return getIFTPayments(state).some(unauthorisedDailyLimitFilter);
};
